import type {
    ViewerAPI,
    ComponentBoundingBox,
    ComponentPadding,
    RuntimeLayout,
    IForceStateParams,
    TransitionDef,
    GridMeasures,
    RuntimePropertyKeys,
    ComponentWithInnerElement
} from '@wix/viewer-manager-interface'
import type {
    BIEvt,
    BIParams,
    BoundingBox,
    Callback,
    CssOverrides,
    Point,
    Pointer,
    CompCssOverrides,
    ScopePointer,
    TransformationOverrides,
    StylesOverrides,
    CompRef,
    ItemUnderXY,
    Rect,
    CanvasAnimationOptions,
    ScrubTriggerVariant,
    ScrubEffectData,
    PossibleDataItems,
    Data,
    GridLayoutOverrides,
    PaddingLayoutOverrides
} from '@wix/document-services-types'
import _ from 'lodash'
import {getViewMode, isSameViewMode} from './structureUtils'
import type {ResponsiveViewerAdapterAPI, ViewerManagerAdapterConfig, ViewerSiteAPI} from './types'
import {buildScopePointerByInflatedId, createScopedPointer, getRootScopeIdById, getScopeById} from './scopesUtils'
import {pointerUtils} from '@wix/document-manager-core'
import {namespaceMapping} from '@wix/document-services-json-schemas'
const {VIEWER_PAGE_DATA_TYPES} = namespaceMapping

const {getPointer} = pointerUtils

export interface ExtendedViewerAPI {
    extractScopeFromPointer(pointer: Pointer): ScopePointer | undefined
    convertIdToScopedPointer(id: string, enableRepeatersInScopes: boolean): Pointer
    getViewMode(): string
    switchModes(pointer: Pointer, modeIdToDeactivate: string, modeIdToActivate: string): void
    activateMode(pointer: Pointer, modeId: string): void
    deactivateMode(pointer: Pointer, modeId: string): void
    registerNavigationComplete(callback: Callback): void
    registerNavigationError(id: string, callback: Callback): void
    setLockedCompsForEnforceAnchors(comps: {[compId: string]: boolean}): void
    setFullLayoutNeeded(b: boolean): void
    forceGenerateAnchors(): void
    setIsGeneratingAnchors(isGenerating: boolean): void
    setIsEnforcingAnchors(isEnforcing: boolean): void
    createChildrenAnchors(parentStructure: any, parentPageId: string, op: {forceMobileStructure: boolean}): void
    unregisterNavigationError(id: string): void
    setPremiumNavigationHandler(callback: Callback): void
    updateData(actions: {pointer: Pointer; value: PossibleDataItems}[]): void
    // it can be undefined for classic
    responsive?: ResponsiveViewerAdapterAPI
}

// maybe it is better to have them as map in future
// this will make possible for us to provide injected config and include not only scopes
// but validate structure. Here for now we are using lodash ability to pick by path
const REQUIRED_SCOPES = [
    'navigation',
    'query',
    'actionsAndBehaviors',
    'updateStatus',
    'component',
    'componentDetector',
    'layout',
    'fonts',
    'livePreview',
    'media',
    'members',
    'velo',
    'pages',
    'platform',
    'animation',
    'scroll',
    'selectiveDownload',
    'stylable',
    'tpa',
    'viewMode',
    'legacy',
    'runtime',
    'runtimeLayout',
    'variants',
    'miniSites',
    'modes.getAllActiveModeIds',
    'modes.updateActiveSOSPModes',
    'modes.switchModes',
    'modes.activateMode',
    'modes.deactivateMode',
    'modes.resetAllActiveModes',
    'byRef.getStructure',
    'rendererModel.getClientSpecMap',
    'rendererModel.notifyAppSpecUpdated',
    'diagnostics',
    'data.getCompIdByDataQueryId',
    'scopes',
    'displayedOnlyComponents',
    'externalComponents',
    'pathBuilder'
]

const getMockedLegacyImplementations = () => ({
    getPageMargins: () => ({bottom: 0, top: 0})
})

const getExtendedViewerAPI = (viewer: ViewerAPI, config?: ViewerManagerAdapterConfig): ExtendedViewerAPI => {
    const convertIdToScopedPointer = (id: string, enableRepeatersInScopes?: boolean) => {
        const pointer = getPointer(id, getViewMode(viewer))
        const scopePath = viewer.scopes.getScope(id)
        const scopedPointer = createScopedPointer(pointer, scopePath, enableRepeatersInScopes)

        return scopedPointer
    }

    const convertIdToPointer = (id: string) => {
        if (!config?.enableScopes) {
            return getPointer(id, getViewMode(viewer))
        }
        return convertIdToScopedPointer(id, config?.enableRepeatersInScopes)
    }

    const convertPointerToId = (compPointer: Pointer) => {
        return compPointer.id
    }

    const extractScopeFromPointer = (pointer: Pointer): ScopePointer | undefined =>
        pointer.scope ??
        getScopeById(viewer, pointer.id, config?.enableRepeatersInScopes) ??
        buildScopePointerByInflatedId(pointer.id)

    const getRootScopeFromPointer = (pointer: Pointer): Pointer | undefined => {
        const rootScope = getRootScopeIdById(viewer, pointer.id, config?.enableRepeatersInScopes)

        if (rootScope) {
            return getPointer(rootScope, pointer.type)
        }

        return
    }

    const tupleArrayToObject = (pointersCssOverrides: CompCssOverrides[]): {[compId: string]: CssOverrides} => {
        return _.reduce(
            pointersCssOverrides,
            (acc, currTuple) => {
                const compId = convertPointerToId(currTuple.compPointer)
                return {...acc, [compId]: currTuple.cssOverrides}
            },
            {}
        )
    }

    /** Convert the result of `viewer.componentDetector.getInnerElementsUnderXY`
     * from the viewer's representation to the editor's representation.
     *
     * - Convert raw ids to pointers
     * - Do not include an innerElementPath property if there are no inner elements
     */
    function convertComponentWithInnerElement({compId, innerElementPath}: ComponentWithInnerElement): ItemUnderXY {
        return {
            compPointer: convertIdToPointer(compId),
            // The first item is always the root comp,
            // so an array that indicates a lack of inner elements is not necessarily empty.
            // It may hold one item
            ...((innerElementPath?.length ?? 0) > 1 ? {innerElementPath} : {})
        }
    }

    function updateData(actions: {pointer: Pointer; value: PossibleDataItems}[]): void {
        viewer.actions.runInBatch(() => {
            for (const action of actions) {
                const {pointer, value} = action
                const {id, type} = pointer
                if (!VIEWER_PAGE_DATA_TYPES[type]) continue
                viewer.data.updateData(VIEWER_PAGE_DATA_TYPES[type] as keyof Data, id, value)
            }
        })
    }

    const extApi = {
        extractScopeFromPointer,
        convertIdToScopedPointer,
        getRootScopeFromPointer,
        getComponentsUnderXY: (x: number, y: number): Pointer[] =>
            viewer.componentDetector.getComponentsUnderXY(x, y).map(convertIdToPointer),
        getItemsUnderXYIncludingInnerElements: (
            x: number,
            y: number
        ): {visibleElements: ItemUnderXY[]; hiddenByBlockingLayer: ItemUnderXY[]} => {
            const innerElementsUnderXY = viewer.componentDetector.getInnerElementsUnderXY(x, y)
            const visibleElements = innerElementsUnderXY.visibleElements.map(convertComponentWithInnerElement)
            const hiddenByBlockingLayer = innerElementsUnderXY.hiddenByBlockingLayer.map(
                convertComponentWithInnerElement
            )
            return {visibleElements, hiddenByBlockingLayer}
        },
        isScrollable: (compPointer: Pointer): boolean => viewer.scroll.isScrollable(convertPointerToId(compPointer)),
        isComponentRenderedOnSite: (compPointer: Pointer): boolean =>
            isSameViewMode(viewer, compPointer.type) &&
            viewer.component.isComponentRenderedOnSite(convertPointerToId(compPointer)),
        getComponentBoundingBox: (compPointer: Pointer): ComponentBoundingBox =>
            viewer.layout.getComponentBoundingBox(convertPointerToId(compPointer)),
        getContainerBoundingBoxes: (
            compPointer: Pointer,
            path: string[],
            containerPropertyName: string
        ): ComponentBoundingBox[] =>
            viewer.layout.getContainerBoundingBoxes(convertPointerToId(compPointer), path, containerPropertyName),
        getOriginalComponentBoundingBox: (compPointer: Pointer): ComponentBoundingBox =>
            viewer.layout.getOriginalComponentBoundingBox(convertPointerToId(compPointer)),
        getComponentInnerElementBoundingBoxes: (compPointer: Pointer, selector: string): ComponentBoundingBox[] =>
            viewer.layout.getComponentInnerElementBoundingBoxes(convertPointerToId(compPointer), selector),
        getRelativeToViewportBoundingBox: (compPointer: Pointer): ComponentBoundingBox =>
            viewer.layout.getRelativeToViewportBoundingBox(convertPointerToId(compPointer)),
        getPadding: (compPointer: Pointer): ComponentPadding =>
            viewer.layout.getPadding(convertPointerToId(compPointer)),
        getBasicMeasureForComp: (compPointer: Pointer): Rect =>
            viewer.layout.getBasicMeasureForComp(convertPointerToId(compPointer)),
        getBasicMeasureForCompWithoutTransform: (compPointer: Pointer): Rect =>
            viewer.layout.getBasicMeasureForCompWithoutTransform(convertPointerToId(compPointer)),
        updateAndPushStart: (compPointer: Pointer): void =>
            viewer.layout.updateAndPushStart(convertPointerToId(compPointer)),
        updateAndPushUpdate: (compPointer: Pointer, runtimeLayout: RuntimeLayout): void =>
            viewer.layout.updateAndPushUpdate(convertPointerToId(compPointer), runtimeLayout),
        updateAndPushEnd: (compPointer: Pointer): void =>
            viewer.layout.updateAndPushEnd(convertPointerToId(compPointer)),
        isShowOnFixedPosition: (compPointer: Pointer): boolean =>
            viewer.layout.isShowOnFixedPosition(convertPointerToId(compPointer)),
        registerStateChange: (listenerId: string, playerPointer: Pointer, callback: Callback): void =>
            viewer.media.registerStateChange(listenerId, convertPointerToId(playerPointer), callback),
        unregisterStateChange: (listenerId: string, playerPointer: Pointer): void =>
            viewer.media.unregisterStateChange(listenerId, convertPointerToId(playerPointer)),
        previewAnimation: (
            compPointer: Pointer,
            pageId: string,
            animationDef: TransitionDef,
            onComplete: Callback
        ): string | null => viewer.animation.previewAnimation(compPointer.id, pageId, animationDef, onComplete),
        responsive: viewer.responsive && {
            ...viewer.responsive,
            getScrollHeight: (compPointer: Pointer): number | null =>
                viewer.responsive.getScrollHeight(convertPointerToId(compPointer)),
            getClientHeight: (compPointer: Pointer): number | null =>
                viewer.responsive.getClientHeight(convertPointerToId(compPointer)),
            getScrollWidth: (compPointer: Pointer): number | null =>
                viewer.responsive.getScrollWidth(convertPointerToId(compPointer)),
            getClientWidth: (compPointer: Pointer): number | null =>
                viewer.responsive.getClientWidth(convertPointerToId(compPointer)),
            getGridMeasures: (compPointer: Pointer): GridMeasures | {} =>
                viewer.responsive.getGridMeasures(convertPointerToId(compPointer)),
            detach: (compPointer: Pointer): void => viewer.responsive.detach(convertPointerToId(compPointer)),
            detachMulti: (compPointers: Pointer[]): void =>
                viewer.responsive.detachMulti(compPointers.map(convertPointerToId)),
            detachContainer: (
                containerPointer: Pointer,
                containerOverrides: CssOverrides,
                childrenOverrides: CompCssOverrides[]
            ): void =>
                viewer.responsive.detachContainer(convertPointerToId(containerPointer), {
                    containerOverrides,
                    childrenOverrides: tupleArrayToObject(childrenOverrides)
                }),
            updateDetached: (compPointer: Pointer, boundingBox: BoundingBox): void =>
                viewer.responsive.updateDetached(convertPointerToId(compPointer), boundingBox),
            updateDetachedStyles: (compPointer: Pointer, stylesOverrides: StylesOverrides): void =>
                viewer.responsive.updateDetachedStyles(convertPointerToId(compPointer), stylesOverrides),
            updateDetachedRotation: (compPointer: Pointer, rotationInDegrees: number): void =>
                viewer.responsive.updateDetachedRotation(convertPointerToId(compPointer), rotationInDegrees),
            updateDetachedTransformation: (
                compPointer: Pointer,
                transformationOverrides: TransformationOverrides
            ): void =>
                viewer.responsive.updateDetachedTransformation(
                    convertPointerToId(compPointer),
                    transformationOverrides
                ),
            updateGridCssOverrides: (compPointer: Pointer, gridLayoutOverrides: GridLayoutOverrides): void =>
                viewer.responsive.updateGridCssOverrides(convertPointerToId(compPointer), gridLayoutOverrides),
            updatePaddingCssOverrides: (compPointer: Pointer, paddingLayoutOverrides: PaddingLayoutOverrides): void =>
                viewer.responsive.updatePaddingCssOverrides(convertPointerToId(compPointer), paddingLayoutOverrides),
            clearCompCssOverrides: (compPointer: Pointer): void =>
                viewer.responsive.clearCompCssOverrides(convertPointerToId(compPointer))
        },
        getViewMode: (): string => getViewMode(viewer),
        switchModes: (compPointer: Pointer, modeIdToDeactivate: string, modeIdToActivate: string): void => {
            viewer.modes.switchModes(convertPointerToId(compPointer), modeIdToDeactivate, modeIdToActivate)
        },
        activateMode: (compPointer: Pointer, modeId: string): void => {
            viewer.modes.activateMode(convertPointerToId(compPointer), modeId)
        },
        deactivateMode: (compPointer: Pointer, modeId: string): void => {
            viewer.modes.deactivateMode(convertPointerToId(compPointer), modeId)
        },
        registerNavigationComplete: (callback: Callback): void => {
            // eslint-disable-next-line promise/no-callback-in-promise
            viewer.navigation.waitForNavigation().then(() => callback())
        },
        registerNavigationError: (id: string, callback: Callback): void => {
            // eslint-disable-next-line promise/no-callback-in-promise
            viewer.navigation.waitForNavigation().catch(() => callback())
        },
        triggerWidgetStateUpdateAfterRefresh: ({
            componentRef,
            widgetViewState
        }: {
            componentRef: CompRef
            widgetViewState: {stateName: string; stateProps?: Record<string, any> | undefined}
        }) =>
            viewer.livePreview.triggerWidgetStateUpdateAfterRefresh({
                compId: convertPointerToId(componentRef),
                widgetViewState
            }),
        getRepeatedComponents: (compPointer: Pointer): Pointer[] =>
            viewer.displayedOnlyComponents.getRepeatedComponents(compPointer.id).map(convertIdToPointer),
        getTotalScroll: (compPointer: Pointer): Point => viewer.scroll.getTotalScroll(compPointer.id),
        scroll: (compPointer: Pointer, x: number, y: number, callback: Callback): void =>
            viewer.scroll.scroll(compPointer.id, x, y, callback),
        scrollBy: (compPointer: Pointer, x: number, y: number, callback: Callback): void =>
            viewer.scroll.scrollBy(compPointer.id, x, y, callback),
        setQuickChanges: (compPointer: Pointer, selector: string, declarationMap: Record<string, string>): void =>
            viewer.stylable.setQuickChanges(compPointer.id, selector, declarationMap),
        revertQuickChanges: (compPointer: Pointer): void => viewer.stylable.revertQuickChanges(compPointer.id),
        forceState: (compPointer: Pointer, forceStateParams: IForceStateParams): void =>
            viewer.stylable.forceState(compPointer.id, forceStateParams),
        revertForceState: (compPointer: Pointer): void => viewer.stylable.revertForceState(compPointer.id),
        setLockedCompsForEnforceAnchors: _.noop,
        setFullLayoutNeeded: _.noop,
        forceGenerateAnchors: _.noop,
        setIsGeneratingAnchors: _.noop,
        setIsEnforcingAnchors: _.noop,
        createChildrenAnchors: _.noop,
        unregisterNavigationError: _.noop,
        setPremiumNavigationHandler: _.noop,
        reportBI: (reportDef: BIEvt, params: BIParams = {}): void => {
            params.viewMode = extApi.getViewMode()
            window.biLogger?.log(reportDef, params)
        },
        refreshCustomStyles: viewer.customCSS?.refresh,
        getRuntimeProperty: (compPointer: Pointer, properties: RuntimePropertyKeys) =>
            viewer.runtimeLayout.getRuntimeProperty(convertPointerToId(compPointer), properties),
        getInnerElementBoundingBoxes: (path: string[]): ComponentBoundingBox[] =>
            viewer.layout.getInnerElementBoundingBoxes(path),
        motion: viewer.motion,
        updateData,
        canvas: {
            play: (compPointer: Pointer, animation: CanvasAnimationOptions) =>
                viewer.canvas.play(convertPointerToId(compPointer), animation),
            cancel: (compPointer: Pointer) => viewer.canvas.cancel(convertPointerToId(compPointer)),
            cancelAll: () => viewer.canvas.cancelAll(),
            update: (compPointer: Pointer, overrides: Partial<CanvasAnimationOptions>) =>
                viewer.canvas.update(convertPointerToId(compPointer), overrides),
            addCustomEffect: (compPointer: Pointer, trigger: ScrubTriggerVariant, animation: ScrubEffectData) =>
                viewer.canvas.addCustomEffect(convertPointerToId(compPointer), trigger, animation),
            updateCustomEffect: (
                compPointer: Pointer,
                trigger: ScrubTriggerVariant,
                animation: Partial<ScrubEffectData>
            ) => viewer.canvas.updateCustomEffect(convertPointerToId(compPointer), trigger, animation),
            removeCustomEffects: (compPointer: Pointer, trigger?: ScrubTriggerVariant) =>
                viewer.canvas.removeCustomEffects(convertPointerToId(compPointer), trigger)
        }
    }

    return extApi
}

export const buildViewerSiteAPI = (viewer: ViewerAPI, config?: ViewerManagerAdapterConfig): ViewerSiteAPI => {
    const value = _(viewer).pick(REQUIRED_SCOPES).values().value()
    const extendedViewerAPI = getExtendedViewerAPI(viewer, config)

    return _.merge({}, getMockedLegacyImplementations(), ...value, extendedViewerAPI)
}
